<!-- eslint-disable
  vuejs-accessibility/mouse-events-have-key-events,
  vuejs-accessibility/no-static-element-interactions,
  vuejs-accessibility/anchor-has-content
-->
<template>
  <div
    class="product-card grid"
    :class="{
      [$class('loading')]: !product,
      'show-secondary-image': showSecondaryImage,
    }"
    @mouseenter="loadSecondaryImage = true"
  >
    <a
      class="product-card__image-container col xs2 l3"
      :href="product.url"
      @focus="loadSecondaryImage = true"
    >
      <responsive-image
        class="product-card__primary-image"
        :image="product.featuredImage"
        min-max="200-500"
        ratio="1:1"
        sizes="(min-width: 1140px) 25vw, 50vw"
      />

      <responsive-image
        v-if="loadSecondaryImage && product.secondaryImage"
        class="product-card__secondary-image"
        :image="product.secondaryImage"
        min-max="200-500"
        :padding="false"
        ratio="1:1"
        sizes="(min-width: 1140px) 25vw, 50vw"
        @image-loaded="showSecondaryImage = true"
      />

      <span
        v-if="!product.featuredImage?.src"
        class="visually-hidden"
        v-text="imageTextFallback"
      />

      <content-badge
        v-if="badgeText"
        :badge-text="badgeText"
        color-scheme="grey"
      >
        <template #icon>
          <icon-bell-fill />
        </template>
      </content-badge>
    </a>

    <div class="product-card__footer col xs4 l4-12">
      <product-vendor
        class="product-card__vendor"
        :product="product"
      />

      <component
        :is="product.url ? 'a' : 'p'"
        class="
          product-card__title
          text-heading-m
          text-heading-l-desktop
        "
        :href="product.url"
      >
        <h3 v-text="product.title" />
      </component>

      <product-prices
        class="product-card__prices"
        :available="product?.available"
        :compare-at-price="product?.compareAtPrice?.amount"
        :price="product?.price?.amount"
        :varies="product?.price?.varies"
      />
    </div>

    <ul class="product-card__pills col xs-span l4-12">
      <template
        v-for="(type, index) of productType?.values"
        :key="`type-${index}`"
      >
        <li
          v-if="getPillText(type) && productType.isOnSale[index] == 'true'"
          class="product-card__pill"
        >
          <a
            class="product-card__pill-link text-body-s-regular"
            :href="product.url"
          >
            <component :is="getPillIcon(type)" />
            <span v-text="getPillText(type)" />
          </a>
        </li>
      </template>

      <li class="product-card__pill product-card__pill--vehicle-details-list">
        <vehicle-details-list
          class="product-card__vehicle-details-list"
          :product="product"
        />
      </li>
    </ul>

    <div class="product-card__actions col xs-span">
      <btn
        class="product-card__cta"
        :label="$string('product.cta.view_product')"
        modifiers="secondary center block"
        :url="product.url"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Product card (product-card)
 * -----------------------------------------------------------------------------
 * Product card used throughout the site.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 *
 */
import Btn from '~global/btn/btn.vue'
import ContentBadge from '~async/content-badge/content-badge'
import ProductPrices from '~global/product-prices/product-prices'
import ProductVendor from '~async/product-vendor/product-vendor'
import ResponsiveImage from '~global/images/responsive-image'
import VehicleDetailsList from '~async/vehicle-details-list/vehicle-details-list'

import IconBellFill from '~icons/general/bell-fill.svg'
import IconBundle from '~icons/general/bundle.svg'
import IconManual from '~icons/general/manual.svg'
import IconPhone from '~icons/general/phone.svg'

export default {
  name: 'ProductCard',

  components: {
    Btn,
    ContentBadge,
    IconBellFill,
    IconBundle,
    IconManual,
    IconPhone,
    ProductPrices,
    ProductVendor,
    ResponsiveImage,
    VehicleDetailsList,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      loadSecondaryImage: false,
      pills: {
        bundle: this.$variable('settings.productOptions.bundle'),
        bundleDownload: this.$variable('settings.productOptions.bundleDownload'),
        digital: this.$variable('settings.productOptions.digital'),
        download: this.$variable('settings.productOptions.download'),
        physical: this.$variable('settings.productOptions.physical'),
      },
      showSecondaryImage: false,
    }
  },

  computed: {

    /**
     * Compute badge text.
     * @returns {String}
     */
    badgeText() {
      if (!this.product) {
        return ''
      }

      return this.product.available ? '' : this.$string('product.prices.sold_out')
    },

    /**
     * Compute product image text fallback - not alt tag just in case an
     * image isn't uploaded.
     * @returns {String}
     */
    imageTextFallback() {
      if (this.product.featuredImage?.src) {
        return ''
      }

      return this.$string('accessibility.product_image_fallback')
        .replace('{{ title }}', this.product.title)
    },

    /**
     * Compute product type from available product options.
     * @returns {Object|undefined}
     */
    productType() {
      const optionObject = this.product?.options?.find((option) => {
        return option.name === 'Type' || option.name === 'Title'
      })

      if (!optionObject) {
        return {}
      }

      const variants = optionObject.values?.map((value) => {
        const match = this.product.variants?.find((variant) => {
          const typeOption = variant.selectedOptions.find((selectedOption) => {
            return selectedOption.name === 'Type' || selectedOption.name === 'Title'
          })

          return typeOption.value === value
        })

        return match
      })

      return {
        ...optionObject,
        variants,
      }
    },
  },

  methods: {

    /**
     * Return product option icon.
     * @param {String} type - Product type option.
     * @returns {VueComponent}
     */
    getPillIcon(type) {
      let icon

      if (this.pills.bundle.includes(type)) {
        icon = IconBundle
      } else if (this.pills.bundleDownload.includes(type)) {
        icon = IconBundle
      } else if (this.pills.physical.includes(type)) {
        icon = IconManual
      } else if (this.pills.digital.includes(type)) {
        icon = IconPhone
      } else if (this.pills.download.includes(type)) {
        icon = IconPhone
      }

      return icon
    },

    /**
     * Return product option text.
     * @param {String} type - Product type option.
     * @returns {String}
     */
    getPillText(type) {
      let text

      if (this.pills.bundle.includes(type)) {
        text = this.$string('product.options.card.bundle')
      } else if (this.pills.bundleDownload.includes(type)) {
        text = this.$string('product.options.card.bundle_download')
      } else if (this.pills.physical.includes(type)) {
        text = this.$string('product.options.card.print')
      } else if (this.pills.digital.includes(type)) {
        text = this.$string('product.options.card.online')
      } else if (this.pills.download.includes(type)) {
        text = this.$string('product.options.card.download')
      }

      return text
    },
  },
}
</script>

<style lang="scss">
@import './product-card';
</style>
